import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getCalendarTasks, getTaskStatuses, getTaskCategories } from '../../Requests/TaskRequests';
import { getTeamUuid } from '../../utils/cookies';
import { useTeamsContext } from '../../Components/ContextProviders/TeamsContextProvider';
import { useSidebar } from '../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../utils/sidebar';
import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
    EllipsisHorizontalIcon,
} from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import UpgradedLoader from '../../Components/UpgradedLoader';

function GlobalCalendar() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isCollapsed } = useSidebar();
    const { currentTeam } = useTeamsContext();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentView, setCurrentView] = useState('month');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [statuses, setStatuses] = useState([]);
    const [categories, setCategories] = useState([]);

    const getEventBackgroundColor = (event) => {
        if (event.category && event.category.color) return event.category.color;
        switch (event.type) {
            case 'task': return '#3B82F6';
            case 'event': return '#10B981';
            case 'deadline': return '#EF4444';
            default: return '#6B7280';
        }
    };

    const getTextColor = (backgroundColor) => {
        let color = backgroundColor.replace('#', '');
        let r = parseInt(color.substring(0, 2), 16);
        let g = parseInt(color.substring(2, 4), 16);
        let b = parseInt(color.substring(4, 6), 16);
        let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    };

    const transformCalendarEvents = (eventsData) => {
        if (!Array.isArray(eventsData)) return [];
        return eventsData.map(event => {
            const eventDate = new Date(event.start);
            let timeStr = '';
            if (event.allDay) {
                timeStr = event.type === 'deadline' ? 'Due' : 'All Day';
            } else {
                timeStr = eventDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                if (event.duration_minutes) {
                    const hours = Math.floor(event.duration_minutes / 60);
                    const minutes = event.duration_minutes % 60;
                    timeStr += hours > 0
                        ? ` (${hours}h${minutes > 0 ? ` ${minutes}m` : ''})`
                        : ` (${minutes}m)`;
                }
            }
            const backgroundColor = getEventBackgroundColor(event);
            const textColor = getTextColor(backgroundColor);
            return {
                id: `calendar-${event.id}`,
                name: event.title,
                time: timeStr,
                datetime: event.start,
                type: event.type,
                priority: event.priority,
                category: event.category,
                backgroundColor,
                textColor,
                task: event,
                video_call_url: event.video_call_url,
                duration_minutes: event.duration_minutes,
                href: '#',
            };
        });
    };

    useEffect(() => {
        const teamUuid = currentTeam?.uuid || getTeamUuid();
        if (!teamUuid) return;

        setLoading(true);

        let startDate, endDate;
        if (currentView === 'week') {
            const weekStart = new Date(currentDate);
            const dayOfWeek = weekStart.getDay();
            const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            weekStart.setDate(weekStart.getDate() + daysToMonday);
            startDate = new Date(weekStart);
            endDate = new Date(weekStart);
            endDate.setDate(endDate.getDate() + 6);
        } else {
            startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        }

        getCalendarTasks({
            team_id: teamUuid,
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0],
        })
            .then((res) => {
                const eventsData = res.data?.events || res.data?.data?.events || [];
                setEvents(transformCalendarEvents(eventsData));
            })
            .catch((err) => {
                console.error('Error loading global calendar:', err);
                setEvents([]);
            })
            .finally(() => setLoading(false));
    }, [currentTeam?.uuid, currentDate, currentView]);

    useEffect(() => {
        const teamUuid = currentTeam?.uuid || getTeamUuid();
        if (!teamUuid) return;
        if (statuses.length === 0) {
            getTaskStatuses(teamUuid).then(r => setStatuses(r.data || [])).catch(() => {});
        }
        if (categories.length === 0) {
            getTaskCategories(teamUuid).then(r => setCategories(r.data || [])).catch(() => {});
        }
    }, [currentTeam?.uuid]);

    const generateMonthDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const today = new Date();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInPrevMonth = new Date(year, month, 0).getDate();
        const days = [];

        for (let i = firstDayOfMonth - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date, day, isCurrentMonth: false, isToday: false, events: [] });
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
            days.push({
                date, day, isCurrentMonth: true, isToday,
                events: events.filter(e => e.datetime.startsWith(date)),
            });
        }
        const remaining = 42 - days.length;
        for (let day = 1; day <= remaining; day++) {
            const date = `${year}-${String(month + 2).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date, day, isCurrentMonth: false, isToday: false, events: [] });
        }
        return days;
    };

    const monthNames = [
        t('Calendar.MonthNames.January'), t('Calendar.MonthNames.February'),
        t('Calendar.MonthNames.March'), t('Calendar.MonthNames.April'),
        t('Calendar.MonthNames.May'), t('Calendar.MonthNames.June'),
        t('Calendar.MonthNames.July'), t('Calendar.MonthNames.August'),
        t('Calendar.MonthNames.September'), t('Calendar.MonthNames.October'),
        t('Calendar.MonthNames.November'), t('Calendar.MonthNames.December'),
    ];

    const handleNavigation = (direction) => {
        const d = new Date(currentDate);
        if (currentView === 'month') {
            d.setMonth(d.getMonth() + direction);
        } else {
            d.setDate(d.getDate() + direction * 7);
        }
        setCurrentDate(d);
    };

    const MonthView = () => {
        const days = generateMonthDays();
        return (
            <div className="shadow ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col h-full">
                <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs/6 font-semibold text-gray-700 lg:flex-none">
                    {[t('Calendar.DayNames.Mon'), t('Calendar.DayNames.Tue'), t('Calendar.DayNames.Wed'),
                      t('Calendar.DayNames.Thu'), t('Calendar.DayNames.Fri'), t('Calendar.DayNames.Sat'),
                      t('Calendar.DayNames.Sun')].map(d => (
                        <div key={d} className="flex justify-center bg-white py-2"><span>{d}</span></div>
                    ))}
                </div>
                <div className="flex bg-gray-200 text-xs/6 text-gray-700 lg:flex-auto flex-1">
                    <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px h-full">
                        {days.map((day, index) => (
                            <div
                                key={`${day.date}-${index}`}
                                data-is-today={day.isToday ? '' : undefined}
                                data-is-current-month={day.isCurrentMonth ? '' : undefined}
                                className="group relative bg-gray-50 px-3 py-3 text-gray-500 data-[is-current-month]:bg-white flex flex-col min-h-[120px]"
                            >
                                <time
                                    dateTime={day.date}
                                    className="relative group-[:not([data-is-current-month])]:opacity-75 [[data-is-today]_&]:flex [[data-is-today]_&]:size-6 [[data-is-today]_&]:items-center [[data-is-today]_&]:justify-center [[data-is-today]_&]:rounded-full [[data-is-today]_&]:bg-iavisus-100 [[data-is-today]_&]:font-semibold [[data-is-today]_&]:text-white"
                                >
                                    {day.day}
                                </time>
                                {day.events.length > 0 && (
                                    <ol className="mt-2 flex-1 space-y-1">
                                        {day.events.slice(0, 3).map((event) => (
                                            <li key={event.id}>
                                                <div
                                                    className="flex flex-col p-2 rounded-md cursor-pointer hover:opacity-80"
                                                    style={{ backgroundColor: event.backgroundColor, color: event.textColor }}
                                                    onClick={() => event.task?.project?.uuid && navigate(`/project/${event.task.project.uuid}/kanban`)}
                                                >
                                                    <p className="text-xs font-medium truncate">{event.name}</p>
                                                    <div className="flex items-center justify-between">
                                                        <time dateTime={event.datetime} className="text-xs opacity-80">{event.time}</time>
                                                        {event.task?.project?.name && (
                                                            <span className="text-xs opacity-70 truncate max-w-[60px]">{event.task.project.name}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                        {day.events.length > 3 && (
                                            <li className="text-xs text-gray-500">+ {day.events.length - 3} more</li>
                                        )}
                                    </ol>
                                )}
                            </div>
                        ))}
                    </div>
                    {/* Mobile grid */}
                    <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden h-full">
                        {days.map((day, index) => (
                            <button
                                key={`${day.date}-mobile-${index}`}
                                type="button"
                                data-is-today={day.isToday ? '' : undefined}
                                data-is-current-month={day.isCurrentMonth ? '' : undefined}
                                className="group relative flex flex-col px-3 py-2 hover:bg-gray-100 focus:z-10 data-[is-current-month]:bg-white [&:not([data-is-current-month])]:bg-gray-50 min-h-[80px]"
                            >
                                <time dateTime={day.date} className="ml-auto">{day.day}</time>
                                {day.events.length > 0 && (
                                    <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                                        {day.events.map((e) => (
                                            <span key={e.id} className="mx-0.5 mb-1 size-1.5 rounded-full bg-gray-400" />
                                        ))}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const WeekView = () => {
        const getWeekDays = () => {
            const today = new Date();
            const weekStart = new Date(currentDate);
            const dayOfWeek = weekStart.getDay();
            const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            weekStart.setDate(weekStart.getDate() + daysToMonday);
            return Array.from({ length: 7 }, (_, i) => {
                const date = new Date(weekStart);
                date.setDate(weekStart.getDate() + i);
                const isToday = today.getDate() === date.getDate() &&
                    today.getMonth() === date.getMonth() &&
                    today.getFullYear() === date.getFullYear();
                return {
                    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
                    date: date.getDate(),
                    fullDate: date.toISOString().split('T')[0],
                    isToday,
                };
            });
        };
        const weekDays = getWeekDays();

        return (
            <div className="isolate flex flex-auto flex-col bg-white h-full">
                <div style={{ width: '165%' }} className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full h-full">
                    <div className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black/5 sm:pr-8">
                        <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm/6 text-gray-500 sm:grid">
                            <div className="col-end-1 w-14" />
                            {weekDays.map((day) => (
                                <div key={day.day} className="flex items-center justify-center py-3">
                                    <span className={day.isToday ? 'flex items-baseline' : ''}>
                                        {day.day}{' '}
                                        <span className={`items-center justify-center font-semibold ${day.isToday ? 'ml-1.5 flex size-8 rounded-full bg-iavisus-100 text-white' : 'text-gray-900'}`}>
                                            {day.date}
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-auto overflow-auto">
                        <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
                        <div className="grid flex-auto grid-cols-1 grid-rows-1 relative">
                            <div style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }} className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100">
                                <div className="row-end-1 h-7" />
                                {Array.from({ length: 24 }, (_, i) => (
                                    <React.Fragment key={i}>
                                        <div>
                                            <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
                                                {i === 0 ? '12AM' : i === 12 ? '12PM' : i > 12 ? `${i - 12}PM` : `${i}AM`}
                                            </div>
                                        </div>
                                        <div />
                                    </React.Fragment>
                                ))}
                            </div>
                            <div className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
                                {Array.from({ length: 8 }, (_, i) => (
                                    <div key={i} className={`col-start-${i + 1} row-span-full`} />
                                ))}
                            </div>
                            <ol style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }} className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8">
                                {events
                                    .filter(event => {
                                        const eventDate = new Date(event.datetime);
                                        const weekStart = new Date(weekDays[0].fullDate);
                                        const weekEnd = new Date(weekDays[6].fullDate);
                                        weekEnd.setHours(23, 59, 59, 999);
                                        return eventDate >= weekStart && eventDate <= weekEnd;
                                    })
                                    .map((event) => {
                                        const eventDate = new Date(event.datetime);
                                        const eventDateStr = event.datetime.split('T')[0];
                                        const dayOfWeek = weekDays.findIndex(d => d.fullDate === eventDateStr);
                                        const hour = eventDate.getHours();
                                        const minute = eventDate.getMinutes();
                                        const startRow = 1 + (hour * 2) + Math.floor(minute / 30);
                                        const spanRows = Math.max(2, Math.ceil((event.duration_minutes || 60) / 30));
                                        return (
                                            <li key={event.id} style={{ gridRow: `${startRow} / span ${spanRows}`, gridColumn: dayOfWeek >= 0 ? dayOfWeek + 1 : 1 }} className="relative mt-px flex">
                                                <div
                                                    className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg p-2 text-xs/5 cursor-pointer hover:opacity-80"
                                                    style={{ backgroundColor: event.backgroundColor, color: event.textColor }}
                                                >
                                                    <p className="order-1 font-semibold">{event.name}</p>
                                                    <p className="opacity-75"><time dateTime={event.datetime}>{event.time}</time></p>
                                                    {event.task?.project?.name && (
                                                        <p className="opacity-60 truncate">{event.task.project.name}</p>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 w-full flex justify-center`}>
            <div className="flex flex-col w-full justify-center items-center px-4">
                {/* Breadcrumb */}
                <div className="mb-6 w-full max-w-7xl">
                    <nav className="flex items-center space-x-2 text-sm text-gray-500">
                        <button onClick={() => navigate('/dashboard')} className="hover:text-gray-700">
                            {t('Dashboard.Title')}
                        </button>
                        <span>›</span>
                        <span className="text-gray-900 font-medium">{t('Dashboard.Calendar')}</span>
                    </nav>
                </div>

                <div className="w-full max-w-7xl h-[calc(100vh-200px)]">
                    <div className="lg:flex lg:h-full lg:flex-col h-full">
                        <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 lg:flex-none">
                            <h1 className="text-base font-semibold text-gray-900">
                                <time dateTime={currentDate.toISOString().slice(0, 7)}>
                                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                                </time>
                            </h1>
                            <div className="flex items-center">
                                <div className="relative flex items-center rounded-md bg-white shadow-sm outline outline-1 -outline-offset-1 outline-gray-300 md:items-stretch">
                                    <button type="button" onClick={() => handleNavigation(-1)} className="flex h-9 w-12 items-center justify-center rounded-l-md pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50">
                                        <span className="sr-only">{t('Calendar.PreviousMonth')}</span>
                                        <ChevronLeftIcon className="size-5" />
                                    </button>
                                    <button type="button" onClick={() => setCurrentDate(new Date())} className="hidden px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block">
                                        {currentView === 'month' ? t('Calendar.ThisMonth') : t('Calendar.Today')}
                                    </button>
                                    <button type="button" onClick={() => handleNavigation(1)} className="flex h-9 w-12 items-center justify-center rounded-r-md pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50">
                                        <span className="sr-only">{t('Calendar.NextMonth')}</span>
                                        <ChevronRightIcon className="size-5" />
                                    </button>
                                </div>
                                <div className="hidden md:ml-4 md:flex md:items-center">
                                    <Menu as="div" className="relative">
                                        <MenuButton type="button" className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                            {currentView === 'month' ? t('Calendar.MonthView') : t('Calendar.WeekView')}
                                            <ChevronDownIcon className="-mr-1 size-5 text-gray-400" />
                                        </MenuButton>
                                        <MenuItems transition className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg outline outline-1 outline-black/5 transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75">
                                            <div className="py-1">
                                                <MenuItem>
                                                    <button onClick={() => setCurrentView('week')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100">
                                                        {t('Calendar.WeekView')}
                                                    </button>
                                                </MenuItem>
                                                <MenuItem>
                                                    <button onClick={() => setCurrentView('month')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100">
                                                        {t('Calendar.MonthView')}
                                                    </button>
                                                </MenuItem>
                                            </div>
                                        </MenuItems>
                                    </Menu>
                                </div>
                                <Menu as="div" className="relative ml-6 md:hidden">
                                    <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
                                        <EllipsisHorizontalIcon className="size-5" />
                                    </MenuButton>
                                    <MenuItems transition className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg outline outline-1 outline-black/5">
                                        <div className="py-1">
                                            <MenuItem><button onClick={() => setCurrentView('week')} className="block w-full text-left px-4 py-2 text-sm text-gray-700">{t('Calendar.WeekView')}</button></MenuItem>
                                            <MenuItem><button onClick={() => setCurrentView('month')} className="block w-full text-left px-4 py-2 text-sm text-gray-700">{t('Calendar.MonthView')}</button></MenuItem>
                                        </div>
                                    </MenuItems>
                                </Menu>
                            </div>
                        </header>

                        {loading ? (
                            <div className="flex items-center justify-center flex-1 py-24">
                                <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-black" />
                            </div>
                        ) : (
                            <>
                                {currentView === 'month' ? <MonthView /> : <WeekView />}
                                {currentView === 'month' && (
                                    <div className="relative px-4 py-10 sm:px-6 lg:hidden">
                                        <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow outline outline-1 outline-black/5">
                                            {events.map((event) => (
                                                <li key={event.id} className="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50">
                                                    <div className="flex-none w-3 h-3 rounded-full mr-3 mt-1" style={{ backgroundColor: event.backgroundColor }} />
                                                    <div className="flex-auto">
                                                        <p className="font-semibold text-gray-900">{event.name}</p>
                                                        <time dateTime={event.datetime} className="mt-2 flex items-center text-gray-700">
                                                            <ClockIcon className="mr-2 size-5 text-gray-400" />
                                                            {event.time}
                                                        </time>
                                                    </div>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GlobalCalendar;
